// Sidebar.tsx
"use client";

import { useState, useEffect } from "react";
import { MenuItem } from "@/core/types/menuApi";
import { useDrawerStoreTolica } from "@/stores/drawerStore";
import {
  Drawer,
  DrawerContent,
} from "@/components/ui/drawer";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Link from "next/link";
import { Heading } from "@/components/ui/Heading";
import { Button } from "@/components/ui/button";
import HeaderProfileLogin from "./HeaderProfileLogin";
import HeaderSelecetLanguage from "./HeaderSelecetLanguage";
import { useDictionary } from "@/core/hooks/use-dictionary";
import { normalizeLink } from "@/core/lib/utils";

type Props = {
  mainItems: MenuItem[];
};

export default function Sidebar({ mainItems }: Props) {
  const { open, stack, push, pop, reset } = useDrawerStoreTolica();
  const { dictionary } = useDictionary()

  const [renderedStack, setRenderedStack] = useState(stack);
  const [prevStack, setPrevStack] = useState(stack);

  if (stack !== prevStack) {
    if (stack.length >= prevStack.length) {
      setPrevStack(stack);
      setRenderedStack(stack);
    }
  }

  useEffect(() => {
    if (stack.length < prevStack.length) {
      const timer = setTimeout(() => {
        setRenderedStack(stack);
        setPrevStack(stack);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [stack, prevStack]);

  const activeIndex = stack.length;

  // اضافه کردن فیلد link برای استفاده در دکمه مشاهده همه
  const panels = [
    { id: "root", title: "منوی اصلی", items: mainItems, isRoot: true, link: "" },
    ...renderedStack.map((item) => ({
      id: item.id,
      title: item.title,
      items: item.children ?? [],
      isRoot: false,
      link: normalizeLink(item.link) || "#", // گرفتن لینک آیتم والد
    })),
  ];

  return (
    <Drawer open={open} onOpenChange={(v) => !v && reset()}>
      <DrawerContent className="h-dvh flex flex-col w-full mx-0 rounded-none rtl:mr-0 rtl:ml-auto ltr:ml-0 ltr:mr-auto overflow-hidden">

        <div className="relative flex-1 overflow-hidden pt-6">
          <div
            className="flex h-full w-full transition-transform duration-300 ease-in-out rtl:translate-x-[calc(var(--step)*100%)] ltr:-translate-x-[calc(var(--step)*100%)]"
            style={{ "--step": activeIndex } as React.CSSProperties}
          >
            {panels.map((panel) => (
              <div
                key={panel.id}
                className="w-full shrink-0 flex flex-col h-full"
              >
                {/* هدر پنل (دکمه برگشت) */}
                {!panel.isRoot && (
                  <div
                    className="flex items-center gap-2 border-b border-primary-100 px-4 pt-2.5 pb-3 cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={pop}
                  >
                    <ChevronLeft className="w-4 h-4 text-primary-400 rtl:rotate-180" />
                    <Heading level={4} className="font-primary text-primary-600">
                      {panel.title}
                    </Heading>
                  </div>
                )}

                <nav className="overflow-y-auto no-scrollbar px-4 pb-3 flex-1">
                  <ul className="flex flex-col gap-y-1">


                    {/* لیست آیتم‌های فرزند */}
                    {panel.items.map((item) => (
                      <li key={item.id}>
                        <div className="flex items-center justify-between h-10 p-2 hover:bg-secondary-50 rounded-md transition-colors">
                          {item.children && item.children.length > 0 ? (
                            <button
                              className="flex-1 text-start h-full"
                              onClick={() => push(item)}
                            >
                              <Heading level={3} className="text-xs">
                                {item.title}
                              </Heading>
                            </button>
                          ) : (
                            <Link
                              href={normalizeLink(item.link) || "#"}
                              className="flex-1 h-full flex items-center"
                              onClick={reset}
                            >
                              <Heading level={3} className="text-xs">
                                {item.title}
                              </Heading>
                            </Link>
                          )}

                          {item.children && item.children.length > 0 && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => push(item)}
                              aria-label={item.title}
                            >
                              <ChevronRight className="w-4 h-4 text-primary-500 rtl:rotate-180" />
                            </Button>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </nav>
              </div>
            ))}
          </div>
        </div>

        <div className="p-4 flex gap-2 justify-between items-center border-t border-primary-700 bg-background z-10">
          <HeaderProfileLogin />
          <HeaderSelecetLanguage />
        </div>
      </DrawerContent>
    </Drawer>
  );
}
